const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: false });
const TERMINATOR = "\r\n";

export const MAC_TIMEOUT_MS = 5000;
export const WIFI_TIMEOUT_MS = 12000;
export const WIFI_MAX_ATTEMPTS = 3;
export const WIFI_RETRY_DELAY_MS = 1500;
export const CREDENTIALS_TIMEOUT_MS = 15000;

export function dataViewToBytes(value) {
  if (!value) return new Uint8Array();
  if (value instanceof Uint8Array) return value;
  return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
}

export function bytesToHex(value) {
  return Array.from(dataViewToBytes(value))
    .map((byte) => byte.toString(16).padStart(2, "0").toUpperCase())
    .join(" ");
}

export function decodeBytes(value) {
  return decoder.decode(dataViewToBytes(value));
}

export function buildCommand(type, values = {}) {
  let text;
  const warnings = [];

  if (type === "mac") text = "{'mac'}\r\n";
  else if (type === "wifi") text = "{'wifi'}\r\n";
  else if (type === "credentials") {
    const ssid = String(values.ssid ?? "");
    const password = String(values.password ?? "");
    const unsafe = (value) => ({
      apostrophe: value.includes("'"),
      backslash: value.includes("\\"),
    });
    const ssidUnsafe = unsafe(ssid);
    const passwordUnsafe = unsafe(password);
    if (
      ssidUnsafe.apostrophe ||
      ssidUnsafe.backslash ||
      passwordUnsafe.apostrophe ||
      passwordUnsafe.backslash
    ) {
      warnings.push({
        code: "unescaped_credential_character",
        ssidContainsApostrophe: ssidUnsafe.apostrophe,
        ssidContainsBackslash: ssidUnsafe.backslash,
        passwordContainsApostrophe: passwordUnsafe.apostrophe,
        passwordContainsBackslash: passwordUnsafe.backslash,
      });
    }
    text = `{'wifi':{'ssid':'${ssid}','psswd':'${password}'}}${TERMINATOR}`;
  } else {
    throw new Error(`Comando SmartDream sconosciuto: ${type}`);
  }

  const bytes = encoder.encode(text);
  return {
    type,
    text,
    bytes,
    view: new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength),
    hex: bytesToHex(bytes),
    warnings,
    redactedText:
      type === "credentials"
        ? `{'wifi':{'ssid':'${String(values.ssid ?? "")}','psswd':'****'}}${TERMINATOR}`
        : text,
  };
}

function concatBytes(left, right) {
  const combined = new Uint8Array(left.length + right.length);
  combined.set(left, 0);
  combined.set(right, left.length);
  return combined;
}

function terminatorIndex(bytes) {
  for (let index = 0; index < bytes.length - 1; index += 1) {
    if (bytes[index] === 0x0d && bytes[index + 1] === 0x0a) return index;
  }
  return -1;
}

export class CrlfNotificationBuffer {
  constructor(maxBytes = 8192) {
    this.maxBytes = maxBytes;
    this.reset();
  }

  reset() {
    this.buffer = new Uint8Array();
  }

  get bufferText() {
    return decodeBytes(this.buffer);
  }

  push(value) {
    const chunk = dataViewToBytes(value);
    this.buffer = concatBytes(this.buffer, chunk);
    if (this.buffer.length > this.maxBytes) {
      throw new Error(`notification_buffer_overflow_${this.maxBytes}`);
    }

    const messages = [];
    let index = terminatorIndex(this.buffer);
    while (index >= 0) {
      const messageBytes = this.buffer.slice(0, index + 2);
      messages.push({
        bytes: messageBytes,
        hex: bytesToHex(messageBytes),
        text: decodeBytes(messageBytes),
      });
      this.buffer = this.buffer.slice(index + 2);
      index = terminatorIndex(this.buffer);
    }

    return {
      byteLength: chunk.length,
      hex: bytesToHex(chunk),
      text: decodeBytes(chunk),
      reconstructedBuffer: decodeBytes(this.buffer),
      reconstructedHex: bytesToHex(this.buffer),
      messages,
    };
  }
}

class DialectParser {
  constructor(source) {
    this.source = source;
    this.index = 0;
  }

  parse() {
    const value = this.parseValue();
    this.skipWhitespace();
    if (this.index !== this.source.length) throw new Error("trailing_data");
    return value;
  }

  skipWhitespace() {
    while (/\s/.test(this.source[this.index] || "")) this.index += 1;
  }

  parseValue() {
    this.skipWhitespace();
    const current = this.source[this.index];
    if (current === "{") return this.parseObject();
    if (current === "[") return this.parseArray();
    if (current === "'" || current === '"') return this.parseString();
    return this.parseAtom();
  }

  parseString() {
    const quote = this.source[this.index];
    this.index += 1;
    let output = "";
    while (this.index < this.source.length) {
      const current = this.source[this.index];
      this.index += 1;
      if (current === "\\") {
        output += current;
        if (this.index < this.source.length) {
          output += this.source[this.index];
          this.index += 1;
        }
        continue;
      }
      if (current === quote) {
        let next = this.index;
        while (/\s/.test(this.source[next] || "")) next += 1;
        if (
          next >= this.source.length ||
          [",", ":", "}", "]"].includes(this.source[next])
        ) {
          this.index = next;
          return output;
        }
      }
      output += current;
    }
    throw new Error("unterminated_string");
  }

  parseObject() {
    const output = {};
    this.index += 1;
    this.skipWhitespace();
    if (this.source[this.index] === "}") {
      this.index += 1;
      return output;
    }
    while (this.index < this.source.length) {
      this.skipWhitespace();
      const quote = this.source[this.index];
      if (quote !== "'" && quote !== '"') throw new Error("invalid_object_key");
      const key = this.parseString();
      this.skipWhitespace();
      if (this.source[this.index] !== ":") throw new Error("missing_colon");
      this.index += 1;
      if (Object.prototype.hasOwnProperty.call(output, key)) {
        throw new Error(`duplicate_key_${key}`);
      }
      output[key] = this.parseValue();
      this.skipWhitespace();
      if (this.source[this.index] === "}") {
        this.index += 1;
        return output;
      }
      if (this.source[this.index] !== ",") throw new Error("missing_comma");
      this.index += 1;
    }
    throw new Error("unterminated_object");
  }

  parseArray() {
    const output = [];
    this.index += 1;
    this.skipWhitespace();
    if (this.source[this.index] === "]") {
      this.index += 1;
      return output;
    }
    while (this.index < this.source.length) {
      output.push(this.parseValue());
      this.skipWhitespace();
      if (this.source[this.index] === "]") {
        this.index += 1;
        return output;
      }
      if (this.source[this.index] !== ",") throw new Error("missing_comma");
      this.index += 1;
    }
    throw new Error("unterminated_array");
  }

  parseAtom() {
    const match = this.source
      .slice(this.index)
      .match(/^(true|false|null|None|-?\d+(?:\.\d+)?)/);
    if (!match) throw new Error("invalid_value");
    this.index += match[0].length;
    if (match[0] === "true") return true;
    if (match[0] === "false") return false;
    if (match[0] === "null" || match[0] === "None") return null;
    return Number(match[0]);
  }
}

export function parseProtocolMessage(message) {
  const body = String(message || "").endsWith(TERMINATOR)
    ? String(message).slice(0, -2)
    : String(message || "");
  const trimmed = body.trim();
  try {
    return JSON.parse(trimmed);
  } catch {
    return new DialectParser(trimmed).parse();
  }
}

function hasBalancedContainers(message) {
  const body = message.endsWith(TERMINATOR) ? message.slice(0, -2) : message;
  const stack = [];
  let quote = null;
  for (let index = 0; index < body.length; index += 1) {
    const current = body[index];
    if (quote) {
      if (current === "\\") {
        index += 1;
        continue;
      }
      if (current === quote) {
        let next = index + 1;
        while (/\s/.test(body[next] || "")) next += 1;
        if (next >= body.length || [",", ":", "}", "]"].includes(body[next])) {
          quote = null;
        }
      }
      continue;
    }
    if (current === "'" || current === '"') quote = current;
    else if (current === "{" || current === "[") stack.push(current);
    else if (current === "}" || current === "]") {
      const expected = current === "}" ? "{" : "[";
      if (stack.pop() !== expected) return false;
    }
  }
  return quote === null && stack.length === 0;
}

export function normalizeMac(mac) {
  const input = String(mac || "").trim();
  if (!/^(?:[0-9a-fA-F]{12}|(?:[0-9a-fA-F]{2}:){5}[0-9a-fA-F]{2})$/.test(input)) {
    return null;
  }
  return input.replace(/:/g, "").toUpperCase();
}

export function parseMacMessage(message) {
  try {
    const parsed = parseProtocolMessage(message);
    const mac = parsed && typeof parsed === "object" ? normalizeMac(parsed.mac) : null;
    if (!mac) return { valid: false, reason: "invalid_mac", parsed };
    return { valid: true, mac, parsed };
  } catch (error) {
    return { valid: false, reason: "parse_error", error: error.message };
  }
}

export function dedupeNetworks(networks) {
  const bySsid = new Map();
  for (const network of networks || []) {
    const entry = {
      ssid: String(network.ssid),
      security: String(network.security),
      rssi: Number.isFinite(Number(network.rssi)) ? Number(network.rssi) : null,
    };
    const previous = bySsid.get(entry.ssid);
    if (!previous || (entry.rssi ?? -999) > (previous.rssi ?? -999)) {
      bySsid.set(entry.ssid, entry);
    }
  }
  return Array.from(bySsid.values()).sort(
    (left, right) => (right.rssi ?? -999) - (left.rssi ?? -999)
  );
}

export function validateWifiMessage(message) {
  const text = String(message || "");
  if (!text.endsWith(TERMINATOR)) {
    return { valid: false, reason: "missing_crlf", networks: [] };
  }
  if (!/^\s*(?:\{'wifi'|\{"wifi")\s*:\s*\[/.test(text)) {
    return { valid: false, reason: "invalid_wifi_prefix", networks: [] };
  }
  if (!/\]\}\r\n$/.test(text)) {
    return { valid: false, reason: "invalid_wifi_suffix", networks: [] };
  }
  if (!hasBalancedContainers(text)) {
    return { valid: false, reason: "unbalanced_containers", networks: [] };
  }

  let parsed;
  try {
    parsed = parseProtocolMessage(text);
  } catch (error) {
    return {
      valid: false,
      reason: "parse_error",
      detail: error.message,
      networks: [],
    };
  }

  if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.wifi)) {
    return { valid: false, reason: "wifi_array_missing", networks: [] };
  }

  for (let index = 0; index < parsed.wifi.length; index += 1) {
    const network = parsed.wifi[index];
    if (!network || typeof network !== "object" || Array.isArray(network)) {
      return { valid: false, reason: `network_${index}_not_object`, networks: [] };
    }
    const keys = Object.keys(network).sort();
    if (keys.join(",") !== "rssi,security,ssid") {
      return { valid: false, reason: `network_${index}_invalid_keys`, networks: [] };
    }
    if (
      typeof network.ssid !== "string" ||
      typeof network.security !== "string" ||
      !["string", "number"].includes(typeof network.rssi)
    ) {
      return { valid: false, reason: `network_${index}_invalid_values`, networks: [] };
    }
  }

  return {
    valid: true,
    reason: "valid",
    networks: dedupeNetworks(parsed.wifi),
    parsed,
  };
}

export function isDeadBeef(value) {
  return bytesToHex(value).replace(/\s/g, "") === "DEADBEEF";
}
